import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getDocuments, uploadDocument, deleteDocument, uploadCompleteCategoryPdf, viewDocument } from '../../services/documentService';
import { DOC_CATEGORIES, COMPLETE_CATEGORY_PDF_SLOT } from '../../constants/documents';
import CategoryCard from '../documents/CategoryCard';

export default function DocumentsTab() {
  const { fbUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(''); // "category:slot"
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, label }
  const [deleting, setDeleting] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [activeTab, setActiveTab] = useState(DOC_CATEGORIES[0]?.key || '');

  const fetchDocs = useCallback(async () => {
    try {
      const res = await getDocuments(fbUser);
      setDocuments(res.data?.data?.documents || []);
    } catch {
      setError('Failed to load documents. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [fbUser]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleUpload = async (category, slotKey, file, customName) => {
    if (file.size > 100 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 100 MB.');
      return;
    }
    setUploadError('');
    setUploading(`${category}:${slotKey}`);
    try {
      await uploadDocument(fbUser, file, category, customName || slotKey);
      await fetchDocs();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading('');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDocument(fbUser, deleteTarget.id);
      setDocuments((prev) => prev.filter((d) => d._id !== deleteTarget.id));
    } catch {
      setUploadError('Delete failed. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await viewDocument(fbUser, doc._id);
      const blob = new Blob([response.data], { type: doc.mimeType || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        // Popup blocked — fallback to download
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.originalName || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('View doc error:', err);
      setUploadError(err.response?.data?.message || 'Failed to open document. Please try again.');
    }
  };

  const handleUploadCompletePdf = async (category, file) => {
    if (file.size > 100 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 100 MB.');
      return;
    }
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are accepted for complete category upload.');
      return;
    }
    setUploadError('');
    setUploading(`${category}:${COMPLETE_CATEGORY_PDF_SLOT}`);
    try {
      await uploadCompleteCategoryPdf(fbUser, file, category);
      await fetchDocs();
    } catch (err) {
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading('');
    }
  };

  const slotDocs = documents.filter((d) => d.slotName !== COMPLETE_CATEGORY_PDF_SLOT);
  const totalSlots = DOC_CATEGORIES.filter((c) => !c.isDynamic).reduce((s, c) => s + c.slots.length, 0);
  const uploadedFixed = slotDocs.filter((d) => d.category !== 'extra_documents').length;
  const uploaded = slotDocs.length;
  const overallPct = Math.round((uploadedFixed / totalSlots) * 100);

  // Per-category counts for tab badges (exclude complete PDFs)
  const getCatCount = (cat) => {
    const catDocs = slotDocs.filter((d) => d.category === cat.key);
    if (cat.isDynamic) return catDocs.length;
    const bySlot = {};
    catDocs.forEach((d) => { if (d.slotName) bySlot[d.slotName] = true; });
    return Object.keys(bySlot).length;
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading your documents...</p>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
        <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <p className="text-sm text-slate-600 font-medium">{error}</p>
      <button onClick={fetchDocs} className="btn-secondary text-sm px-4 py-2">Retry</button>
    </div>
  );

  const activeCategory = DOC_CATEGORIES.find((c) => c.key === activeTab) || DOC_CATEGORIES[0];
  const activeDocs = documents.filter((d) => d.category === activeCategory.key);

  return (
    <div className="space-y-4">
      {/* Overall progress banner */}
      <div className="card px-5 py-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-slate-700">Document Progress</p>
            <p className="text-xs text-slate-400 mt-0.5">{uploaded} of {totalSlots} documents uploaded</p>
          </div>
          <span className={`text-2xl font-bold ${overallPct === 100 ? 'text-emerald-600' : 'text-brand-600'}`}>
            {overallPct}%
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              overallPct === 100 ? 'bg-emerald-500' : 'bg-brand-500'
            }`}
            style={{ width: `${overallPct}%` }}
          />
        </div>
        {overallPct === 100 && (
          <p className="mt-2 text-xs text-emerald-600 font-medium flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            All documents submitted! Our team will review them shortly.
          </p>
        )}
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          </svg>
          {uploadError}
          <button onClick={() => setUploadError('')} className="ml-auto text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Horizontal category tabs */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-1.5 min-w-max pb-1">
          {DOC_CATEGORIES.map((cat) => {
            const count = getCatCount(cat);
            const total = cat.isDynamic ? null : cat.slots.length;
            const isActive = activeTab === cat.key;
            const isComplete = cat.isDynamic ? count > 0 : count === total;

            return (
              <button
                key={cat.key}
                onClick={() => setActiveTab(cat.key)}
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                {cat.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  isActive
                    ? isComplete ? 'bg-emerald-400 text-white' : 'bg-white/20 text-white'
                    : isComplete ? 'bg-emerald-100 text-emerald-700' : count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  {cat.isDynamic ? count : `${count}/${total}`}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active category card */}
      <CategoryCard
        category={activeCategory}
        documents={activeDocs}
        uploading={uploading}
        onUpload={handleUpload}
        onDelete={(id, label) => setDeleteTarget({ id, label })}
        onView={handleView}
        onUploadCompletePdf={handleUploadCompletePdf}
      />

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div className="card max-w-sm w-full p-6 animate-slide-up">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-800 mb-1">Delete document?</h3>
            <p className="text-sm text-slate-500 mb-6">
              "{deleteTarget.label}" will be permanently deleted. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="btn-secondary flex-1 text-sm py-2"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl px-4 py-2 text-sm transition-all duration-200 disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Deleting...
                  </>
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
